export type Summary = {
  users: number
  new_users_7d: number
  new_users_30d: number
  active_subscriptions: number
  active_trials: number
  watchlist_items: number
  presets: number
  events_24h: number
  device_tokens: number
  notification_messages_24h: number
  storekit_transactions: number
  active_positions: number
  pending_price_alerts: number
  analyze_runs_24h: number
  collector_health?: CollectorHealth | null
  generated_at: string
}

export type CollectorHealth = {
  last_scan_at?: string
  last_scan_symbols: number
  degraded_until?: string
  schwab_token_exp?: string
  updated_at?: string
}

export type AdminUser = {
  id: string
  email: string
  apple_sub: string
  auth_provider: string
  guest_install_id?: string
  merged_into_user_id?: string
  created_at: string
  last_seen_at?: string
  subscription_status: string
  product_id?: string
  environment?: string
  expires_at?: string
  trial_used_at?: string
  watchlist_count: number
  preset_count: number
  device_token_count: number
  notification_message_count: number
  open_position_count: number
  storekit_transaction_count: number
  pending_price_alert_count: number
  analyze_run_count: number
}

export type AdminWatchlistItem = {
  id: number
  symbol: string
  position: number
  created_at: string
}

export type AdminPreset = {
  id: number
  name: string
  filters: Record<string, unknown>
  is_default: boolean
  created_at: string
}

export type AdminUserDetail = {
  user: AdminUser
  watchlist: AdminWatchlistItem[]
  presets: AdminPreset[]
  device_tokens: AdminDeviceToken[]
  storekit_transactions: AdminStoreKitTransaction[]
  storekit_restore_events: AdminStoreKitRestoreEvent[]
  notification_preferences: AdminNotificationPreference[]
  notification_messages: AdminNotificationMessage[]
  positions: AdminPosition[]
  price_alerts: AdminPriceAlert[]
  analyze_runs: AdminAnalyzeRun[]
}

export type AdminDeviceToken = {
  token_preview: string
  platform: string
  bundle_id: string
  environment: string
  registered_at: string
  last_seen_at: string
  client_ip?: string
  user_agent?: string
}

export type AdminStoreKitTransaction = {
  original_txn_id: string
  user_id?: string
  user_email?: string
  auth_provider?: string
  app_install_id?: string
  product_id: string
  status: string
  expires_at: string
  environment: string
  first_seen_at: string
  last_seen_at: string
  last_verified_at?: string
}

export type AdminSubscriptionLedgerItem = {
  original_txn_id: string
  current_user_id?: string
  current_user_email?: string
  current_auth_provider?: string
  current_app_install_id?: string
  product_id: string
  status: string
  expires_at: string
  environment: string
  first_seen_at: string
  last_seen_at: string
  last_verified_at?: string
  restore_event_count: number
  restored_user_count: number
  restored_install_count: number
  last_restore_at?: string
  restore_events: AdminStoreKitRestoreEvent[]
}

export type AdminStoreKitRestoreEvent = {
  id: number
  original_txn_id?: string
  user_id?: string
  user_email?: string
  auth_provider?: string
  app_install_id?: string
  source: string
  product_id: string
  status: string
  expires_at: string
  environment: string
  restored_at: string
  client_ip?: string
  user_agent?: string
}

export type AdminOptionUniverseItem = {
  symbol: string
  enabled: boolean
  priority: number
  source: string
  notes: string
  category_id?: number
  category_slug?: string
  category_name?: string
  created_at: string
  updated_at: string
  last_aggregate_at?: string
  rows_30d: number
  uoa_alerts_7d: number
}

export type AdminOptionUniverseCategory = {
  id: number
  slug: string
  name: string
  description: string
  position: number
  is_system: boolean
  symbol_count: number
  enabled_count: number
  created_at: string
  updated_at: string
}

export type AdminCrawlerSettings = {
  rate_per_min: number
  burst: number
  workers: number
  min_pass_gap_sec: number
  pass_timeout_sec: number
  schwab_cap_per_min: number
  updated_at?: string
}

export type AdminUOAOverview = {
  summary: {
    alerts_today: number
    alerts_7d: number
    latest_trade_date?: string
    latest_detected_at?: string
  }
  top_symbols: Array<{
    symbol: string
    alerts_7d: number
    max_multiple: number
    total_volume: number
  }>
  recent_alerts: Array<{
    trade_date: string
    detected_at: string
    symbol: string
    strike: string
    expiration: string
    dte?: number
    side: string
    today_volume: number
    baseline_avg_30d: string
    baseline_avg_7d?: string
    multiple: number
    description?: string
  }>
}

export type AdminNotificationPreference = {
  kind: string
  enabled: boolean
  updated_at: string
}

export type AdminNotificationMessage = {
  id: string
  kind: string
  symbol?: string
  title: string
  message: string
  deep_link?: string
  payload?: Record<string, unknown>
  created_at: string
}



export type AdminPosition = {
  id: string
  symbol: string
  side: string
  strike: string
  expiry: string
  premium: string
  qty: number
  opened_at: string
  status: string
  closed_at?: string
  rolled_to_id?: string
  notes?: string
}

export type AdminPriceAlert = {
  id: number
  symbol: string
  side: string
  target_price: string
  status: string
  created_at: string
  triggered_at?: string
  last_seen_price?: string
  last_checked_at?: string
}

export type AdminAnalyzeRun = {
  id: number
  session_id: string
  symbol: string
  mode: string
  status: string
  final_action?: string
  weighted_score?: string
  started_at: string
  finished_at?: string
}

export type AdminUserUpdate = {
  email?: string
  subscription_status?: string
  product_id?: string
  environment?: string
  expires_at?: string
}

export type LLMConfig = {
  provider: string
  base_url: string
  model: string
  api_key_configured: boolean
  api_key_preview?: string
}

export type AdminSetting = {
  key: string
  group: string
  label: string
  value: unknown
  is_secret: boolean
  source: string
  updated_at?: string
}

export type AdminConfig = {
  llm: LLMConfig
  settings: AdminSetting[]
}

export type CollectorEvent = {
  id: number
  ts: string
  kind: string
  symbol?: string
  detail?: Record<string, unknown>
}

export type AdminAuditEvent = {
  id: number
  ts: string
  actor: string
  action: string
  target: string
  detail?: Record<string, unknown>
}

export type SignupTrial = {
  enabled: boolean
  start_at?: string
  end_at?: string
  days: number
  product_id: string
  eligible_users: number
  granted_users: number
  active_trial_users: number
  updated_at?: string
}

export type AdminWebSubscription = {
  user_id: string
  user_email: string
  auth_provider: string
  product_id: string
  original_txn_id: string
  status: 'active' | 'expired' | 'revoked'
  environment: string
  expires_at: string
  last_verified_at: string
  cancel_at_period_end: boolean
}

export type AdminPromoCode = {
  code: string
  pro_days: number
  max_redemptions?: number | null
  redemptions_count: number
  enabled: boolean
  valid_from?: string | null
  expires_at?: string | null
  notes?: string
  created_by?: string
  created_at: string
  updated_at: string
}

export type AdminPromoCodeCreate = {
  code: string
  pro_days: number
  max_redemptions?: number | null
  enabled?: boolean
  valid_from?: string | null
  expires_at?: string | null
  notes?: string
}

export type AdminPromoCodeUpdate = {
  pro_days?: number
  max_redemptions?: number | null
  enabled?: boolean
  valid_from?: string | null
  expires_at?: string | null
  notes?: string
  clear_max_redemptions?: boolean
  clear_valid_from?: boolean
  clear_expires_at?: boolean
}

export type PasskeyStatus = {
  registered: boolean
}

export type PasskeyLogin = {
  expires_at: string
  token: string
}

export type AdminSession = {
  authenticated: boolean
  generated_at: string
}

export type ProductEventName =
  | 'scan_run'
  | 'scan_result_opened'
  | 'symbol_detail_opened'
  | 'analyze_started'
  | 'attention_item_opened'
  | 'position_reviewed'
  | 'roll_candidates_viewed'

export type AdminMetricsWeek = {
  week_start: string
  active_users: number
  qualified_users: number
  qualified_sessions: number
  sessions: number
  events: Record<ProductEventName, number>
}

const ADMIN_TOKEN_KEY = 'navoption.admin.token'

export function hasStoredAdminToken(): boolean {
  return Boolean(sessionStorage.getItem(ADMIN_TOKEN_KEY))
}

type Envelope<T> = {
  ok: boolean
  data?: T
  error?: { code: string; message: string }
}

export class AdminAPI {
  constructor(private readonly baseURL: string) {}

  async summary(): Promise<Summary> {
    return this.request<Summary>('/v1/admin/summary')
  }

  async session(): Promise<AdminSession> {
    return this.request<AdminSession>('/v1/admin/session')
  }

  async passkeyStatus(): Promise<PasskeyStatus> {
    return this.request<PasskeyStatus>('/v1/admin/passkey/status', {}, false)
  }

  async startPasskeyRegistration(setupKey: string): Promise<unknown> {
    return this.request<unknown>('/v1/admin/passkey/register/start', {
      method: 'POST',
      body: JSON.stringify({ setup_key: setupKey }),
    }, false)
  }

  async finishPasskeyRegistration(credential: unknown): Promise<PasskeyStatus> {
    return this.request<PasskeyStatus>('/v1/admin/passkey/register/finish', {
      method: 'POST',
      body: JSON.stringify(credential),
    }, false)
  }

  async startPasskeyLogin(): Promise<unknown> {
    return this.request<unknown>('/v1/admin/passkey/login/start', {
      method: 'POST',
      body: JSON.stringify({}),
    }, false)
  }

  async finishPasskeyLogin(credential: unknown): Promise<PasskeyLogin> {
    const result = await this.request<PasskeyLogin>('/v1/admin/passkey/login/finish', {
      method: 'POST',
      body: JSON.stringify(credential),
    }, false)
    if (result.token) {
      sessionStorage.setItem(ADMIN_TOKEN_KEY, result.token)
    }
    return result
  }

  async logout(): Promise<void> {
    try {
      await this.request('/v1/admin/passkey/logout', { method: 'POST' }, false)
    } finally {
      sessionStorage.removeItem(ADMIN_TOKEN_KEY)
    }
  }

  async users(query: string, tier = ''): Promise<AdminUser[]> {
    const params = new URLSearchParams({ limit: '100' })
    if (query.trim()) params.set('q', query.trim())
    if (tier.trim()) params.set('tier', tier.trim())
    const data = await this.request<{ items: AdminUser[] }>('/v1/admin/users?' + params.toString())
    return data.items ?? []
  }

  async subscriptions(query: string, status = ''): Promise<AdminSubscriptionLedgerItem[]> {
    const params = new URLSearchParams({ limit: '120' })
    if (query.trim()) params.set('q', query.trim())
    if (status.trim()) params.set('status', status.trim())
    const data = await this.request<{ items: AdminSubscriptionLedgerItem[] }>('/v1/admin/subscriptions?' + params.toString())
    return data.items ?? []
  }

  async optionUniverse(query: string): Promise<AdminOptionUniverseItem[]> {
    const params = new URLSearchParams({ limit: '1000', include_counts: 'false' })
    if (query.trim()) params.set('q', query.trim())
    const data = await this.request<{ items: AdminOptionUniverseItem[] }>('/v1/admin/options/universe?' + params.toString())
    return data.items ?? []
  }

  async upsertOptionSymbols(payload: { symbol?: string; symbols?: string[]; enabled?: boolean; priority?: number; notes?: string; category_id?: number | null }): Promise<AdminOptionUniverseItem[]> {
    const data = await this.request<{ items: AdminOptionUniverseItem[] }>('/v1/admin/options/universe', {
      method: 'POST',
      body: JSON.stringify(payload),
    })
    return data.items ?? []
  }

  async deleteOptionSymbol(symbol: string): Promise<void> {
    await this.request('/v1/admin/options/universe/' + encodeURIComponent(symbol), { method: 'DELETE' })
  }

  async bulkMoveOptionSymbols(symbols: string[], categoryID: number | null): Promise<void> {
    await this.request('/v1/admin/options/universe/bulk-category', {
      method: 'POST',
      body: JSON.stringify({ symbols, category_id: categoryID }),
    })
  }

  async optionCategories(): Promise<AdminOptionUniverseCategory[]> {
    const data = await this.request<{ items: AdminOptionUniverseCategory[] }>('/v1/admin/options/categories')
    return data.items ?? []
  }

  async createOptionCategory(payload: { slug?: string; name: string; description?: string; position?: number }): Promise<AdminOptionUniverseCategory[]> {
    const data = await this.request<{ items: AdminOptionUniverseCategory[] }>('/v1/admin/options/categories', {
      method: 'POST',
      body: JSON.stringify(payload),
    })
    return data.items ?? []
  }

  async updateOptionCategory(id: number, payload: { name?: string; description?: string; position?: number }): Promise<AdminOptionUniverseCategory[]> {
    const data = await this.request<{ items: AdminOptionUniverseCategory[] }>('/v1/admin/options/categories/' + id, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    })
    return data.items ?? []
  }

  async deleteOptionCategory(id: number, reassignTo: number | null): Promise<void> {
    const path = '/v1/admin/options/categories/' + id +
      (reassignTo ? '?reassign_to=' + reassignTo : '')
    await this.request(path, { method: 'DELETE' })
  }

  async crawlerSettings(): Promise<AdminCrawlerSettings> {
    return this.request<AdminCrawlerSettings>('/v1/admin/options/crawler-settings')
  }

  async updateCrawlerSettings(payload: Partial<Pick<AdminCrawlerSettings,
    'rate_per_min' | 'burst' | 'workers' | 'min_pass_gap_sec' | 'pass_timeout_sec'>>): Promise<AdminCrawlerSettings> {
    return this.request<AdminCrawlerSettings>('/v1/admin/options/crawler-settings', {
      method: 'PATCH',
      body: JSON.stringify(payload),
    })
  }

  async uoaOverview(): Promise<AdminUOAOverview> {
    return this.request<AdminUOAOverview>('/v1/admin/uoa/overview')
  }

  async uoaDetectNow(reason: string): Promise<void> {
    const params = new URLSearchParams()
    if (reason.trim()) params.set('reason', reason.trim())
    await this.request('/v1/admin/uoa/detect-now?' + params.toString(), { method: 'POST' })
  }

  async user(userID: string): Promise<AdminUserDetail> {
    return this.request<AdminUserDetail>('/v1/admin/users/' + encodeURIComponent(userID))
  }

  async updateUser(userID: string, payload: AdminUserUpdate): Promise<AdminUserDetail> {
    return this.request<AdminUserDetail>('/v1/admin/users/' + encodeURIComponent(userID), {
      method: 'PUT',
      body: JSON.stringify(payload),
    })
  }

  async grantPro(userID: string): Promise<void> {
    await this.request('/v1/admin/users/' + encodeURIComponent(userID) + '/grant-pro', {
      method: 'POST',
      body: JSON.stringify({ product_id: 'admin.pro', days: 30 }),
    })
  }

  async revokePro(userID: string): Promise<void> {
    await this.request('/v1/admin/users/' + encodeURIComponent(userID) + '/revoke-pro', {
      method: 'POST',
    })
  }

  async webSubscriptions(query = '', status = '', source = ''): Promise<AdminWebSubscription[]> {
    const params = new URLSearchParams()
    if (query.trim()) params.set('q', query.trim())
    if (status) params.set('status', status)
    if (source) params.set('source', source)
    const data = await this.request<{ items: AdminWebSubscription[] }>('/v1/admin/subscriptions/web?' + params.toString())
    return data.items ?? []
  }

  async resetTrialUsed(userID: string): Promise<void> {
    await this.request('/v1/admin/users/' + encodeURIComponent(userID) + '/reset-trial', {
      method: 'POST',
    })
  }

  async config(): Promise<AdminConfig> {
    return this.request<AdminConfig>('/v1/admin/config')
  }

  async signupTrial(): Promise<SignupTrial> {
    return this.request<SignupTrial>('/v1/admin/promotions/signup-trial')
  }

  async updateSignupTrial(payload: Pick<SignupTrial, 'enabled' | 'days' | 'product_id'> & { start_at?: string; end_at?: string }): Promise<SignupTrial> {
    return this.request<SignupTrial>('/v1/admin/promotions/signup-trial', {
      method: 'PUT',
      body: JSON.stringify(payload),
    })
  }

  async promoCodes(): Promise<AdminPromoCode[]> {
    const data = await this.request<{ items: AdminPromoCode[] }>('/v1/admin/promo-codes')
    return data.items ?? []
  }

  async createPromoCode(payload: AdminPromoCodeCreate): Promise<void> {
    await this.request('/v1/admin/promo-codes', {
      method: 'POST',
      body: JSON.stringify(payload),
    })
  }

  async updatePromoCode(code: string, payload: AdminPromoCodeUpdate): Promise<void> {
    await this.request('/v1/admin/promo-codes/' + encodeURIComponent(code), {
      method: 'PATCH',
      body: JSON.stringify(payload),
    })
  }

  async deletePromoCode(code: string): Promise<void> {
    await this.request('/v1/admin/promo-codes/' + encodeURIComponent(code), {
      method: 'DELETE',
    })
  }

  async updateConfig(items: Array<{ key: string; value: unknown; is_secret: boolean }>): Promise<AdminConfig> {
    return this.request<AdminConfig>('/v1/admin/config', {
      method: 'PUT',
      body: JSON.stringify({ items }),
    })
  }

  async events(): Promise<CollectorEvent[]> {
    const data = await this.request<{ items: CollectorEvent[] }>('/v1/admin/collector/events?limit=80')
    return data.items ?? []
  }

  async metrics(): Promise<AdminMetricsWeek[]> {
    const data = await this.request<{ weeks: AdminMetricsWeek[] }>('/v1/admin/metrics?weeks=8')
    return data.weeks ?? []
  }

  async auditEvents(): Promise<AdminAuditEvent[]> {
    const data = await this.request<{ items: AdminAuditEvent[] }>('/v1/admin/audit/events?limit=120')
    return data.items ?? []
  }

  async crawlNow(reason: string): Promise<void> {
    const params = new URLSearchParams()
    if (reason.trim()) params.set('reason', reason.trim())
    await this.request('/v1/admin/crawl-now?' + params.toString(), { method: 'POST' })
  }

  private async request<T>(path: string, init: RequestInit = {}, _requireAuth = true): Promise<T> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(init.headers as Record<string, string> ?? {}),
    }
    const token = sessionStorage.getItem(ADMIN_TOKEN_KEY)
    if (token) headers['Authorization'] = 'Bearer ' + token
    const response = await fetch(this.baseURL.replace(/\/+$/, '') + path, {
      ...init,
      credentials: 'include',
      headers,
    })
    const text = await response.text()
    let body: Envelope<T> = {} as Envelope<T>
    if (text) {
      try {
        body = JSON.parse(text) as Envelope<T>
      } catch (parseErr) {
        if (response.status === 401) sessionStorage.removeItem(ADMIN_TOKEN_KEY)
        const preview = text.length > 200 ? text.slice(0, 200) + '...' : text
        throw new Error(`Bad JSON from ${path} (${response.status}): ${(parseErr as Error).message} | body: ${preview}`)
      }
    }
    if (!response.ok || body.ok === false) {
      if (response.status === 401) sessionStorage.removeItem(ADMIN_TOKEN_KEY)
      throw new Error(body.error?.message || `${response.status} ${response.statusText}`)
    }
    return body.data as T
  }
}
